import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "../SchedulingStyles/DateSelector.css";
import FormatUrl from "../../../utils/UrlFormatter";
import moment from "moment";

const DateSelector = ({ clinicId, practitionerId, csrfToken, onSelect }) => {
  const { t } = useTranslation();
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [showArrows, setShowArrows] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!clinicId || !practitionerId || !csrfToken) return;

      setLoading(true);
      setNoData(false);

      try {
        const url = FormatUrl(
          `/get-slot/?clinic_id=${clinicId}&practitioner_id=${practitionerId}`
        );
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
        });

        const data = await response.json();

        if (data?.slots && Array.isArray(data.slots)) {
          const availableSlots = data.slots.filter(
            (slot) =>
              slot.status === "AV" && moment(slot.end).isAfter(moment())
          );

          const uniqueDates = [
            ...new Set(
              availableSlots.map((slot) =>
                moment(slot.start).format("YYYY-MM-DD")
              )
            ),
          ];

          const formattedDates = uniqueDates.map((dateStr) => ({
            label: moment(dateStr).format("ddd, MMM D"),
            value: dateStr,
          }));

          setDates(formattedDates);
          setNoData(formattedDates.length === 0);
        } else {
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching slot dates:", error);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [clinicId, practitionerId, csrfToken]);

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setShowArrows(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [dates]);

  const handleSelect = (date) => {
    setSelectedDate(date.value);
    onSelect(date.value);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -120 : 120,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="date-selector-container">
      <p>{t("admin_scheduling_new.select_date_label")}</p>

      {loading && (
        <p className="loading-text">{t("admin_scheduling_new.loading_dates")}</p>
      )}
      {!loading && noData && (
        <p className="no-data-text">
          {t("admin_scheduling_new.no_dates_available")}
        </p>
      )}

      {!loading && !noData && dates.length > 0 && (
        <div className="date-slider-wrapper">
          {showArrows && (
            <button className="scroll-btn left" onClick={() => scroll("left")}>
              ←
            </button>
          )}

          <div
            className={`date-scroll ${!showArrows ? "centered" : ""}`}
            ref={scrollRef}
          >
            {dates.map((date) => (
              <div
                key={date.value}
                className={`date-block ${
                  selectedDate === date.value ? "selected" : ""
                }`}
                onClick={() => handleSelect(date)}
              >
                {date.label}
              </div>
            ))}
          </div>

          {showArrows && (
            <button className="scroll-btn right" onClick={() => scroll("right")}>
              →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DateSelector;
